import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Content,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
} from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminGpuActions } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuRefundRequest, VerdaGpuServer, VerdaGpuServerUsageRow } from '../../../types/models/Verda';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import { refundAllRange, refundFromRange, refundPreview, refundStartCandidates } from './verdaGpuServers';

interface VerdaRefundServerModalProps {
  server: VerdaGpuServer;
  /** The detail's hour rows (the newest 200). */
  usage: VerdaGpuServerUsageRow[];
  onClose: () => void;
  /** Something may have changed: the detail refetches. */
  onChanged: () => void;
}

/** The partial-failure refusal: some hours were refunded, then one failed — re-sending continues. */
const PARTIAL_FAILURE_RE = /hour\(s\) refunded, then one FAILED/;

/**
 * `POST /admin/verda/servers/{id}/refund` — credits the customer's wallet with paid hours of a finished
 * server, newest first, each once (no undo). Either every refundable hour (`{ all: true }`, which
 * stops at the first INVOICED hour) or a range from a chosen hour to the newest (`{ from_usage_id }`,
 * refused if it reaches an invoiced hour — so only hours newer than the newest invoiced one are
 * offered). The preview sums the unrefunded `debit_ttc` of the range exactly. Mounted only while open.
 */
function VerdaRefundServerModal({ server, usage, onClose, onChanged }: VerdaRefundServerModalProps) {
  const { refund, busy } = useAdminGpuActions();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [mode, setMode] = useState<'all' | 'from'>('all');
  const candidates = useMemo(() => refundStartCandidates(usage), [usage]);
  const [fromId, setFromId] = useState<number | null>(
    candidates.length > 0 ? candidates[candidates.length - 1].id : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<VerdaGpuRefundRequest | null>(null);

  const range = useMemo(() => {
    if (mode === 'all') {
      return refundAllRange(usage);
    }
    return fromId === null ? [] : (refundFromRange(usage, fromId) ?? []);
  }, [mode, fromId, usage]);
  const preview = refundPreview(range);
  // The detail carries the newest 200 hour rows only; the summary counts them all.
  const truncated = server.usage.hours > usage.length;
  const isPartialFailure = error !== null && PARTIAL_FAILURE_RE.test(error);

  const send = async (request: VerdaGpuRefundRequest) => {
    setError(null);
    setLastRequest(request);
    const outcome = await refund(server.id, request);
    if (outcome.status === 'error') {
      setError(outcome.message);
      addNotification({
        title: `Could not refund ${server.hostname}`,
        srTitle: 'Error alert',
        description: outcome.message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      // A partial run refunded some hours: show them.
      if (PARTIAL_FAILURE_RE.test(outcome.message)) {
        onChanged();
      }
      return;
    }
    const { refunded } = outcome.data;
    addNotification({
      title:
        refunded > 0 ? `${server.hostname}: ${refunded} hour(s) refunded` : `${server.hostname}: nothing to refund`,
      srTitle: 'Success alert',
      description:
        refunded > 0
          ? "The hours were credited to the customer's balance (wallet line “Remboursement — serveur …”)."
          : 'Every hour in the range was already refunded or invoiced.',
      variant: 'success',
    });
    setDrawerOpen(true);
    onChanged();
    onClose();
  };

  const currentRequest: VerdaGpuRefundRequest | null =
    mode === 'all' ? { all: true } : fromId === null ? null : { from_usage_id: fromId };

  return (
    <Modal variant="medium" isOpen onClose={busy ? undefined : onClose} aria-labelledby="verda-refund-title">
      <ModalHeader
        title="Refund hours"
        labelId="verda-refund-title"
        titleIconVariant="warning"
        description={server.hostname}
      />
      <ModalBody>
        {error !== null && (
          <Alert
            variant="danger"
            isInline
            title="Could not refund the hours"
            className="pf-v6-u-mb-md"
            actionLinks={
              isPartialFailure && lastRequest !== null ? (
                <AlertActionLink onClick={() => void send(lastRequest)}>Re-send</AlertActionLink>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}
        <Content component="p">
          Refunded hours are credited to {server.client.name ?? `client #${server.client.id}`}&apos;s prepaid balance,
          newest first, each once. There is no undo. An invoiced hour cannot be refunded here: it takes a credit note in
          Facturier.
        </Content>
        <Form onSubmit={(event) => event.preventDefault()}>
          <FormGroup role="radiogroup" fieldId="verda-refund-mode" label="What to refund" isStack>
            <Radio
              id="verda-refund-all"
              name="verda-refund-mode"
              label="All refundable hours"
              description="Every hour not refunded yet, newest first, stopping at the first invoiced hour."
              isChecked={mode === 'all'}
              onChange={() => setMode('all')}
            />
            <Radio
              id="verda-refund-from"
              name="verda-refund-mode"
              label="From this hour to the newest"
              description="Every hour not refunded yet from the chosen hour up to the newest."
              isChecked={mode === 'from'}
              isDisabled={candidates.length === 0}
              onChange={() => setMode('from')}
            />
          </FormGroup>
          {mode === 'from' && (
            <FormGroup label="Oldest hour to refund" fieldId="verda-refund-from-hour" isRequired>
              <FormSelect
                id="verda-refund-from-hour"
                value={fromId ?? ''}
                onChange={(_event, value) => setFromId(Number(value))}
                aria-label="Oldest hour to refund"
              >
                {candidates.map((row) => (
                  <FormSelectOption
                    key={row.id}
                    value={row.id}
                    label={`${formatDateTime(row.hour_start)} — hour ${row.hour_index} — ${formatPrice(row.debit_ttc)}`}
                  />
                ))}
              </FormSelect>
            </FormGroup>
          )}
        </Form>
        <Alert
          variant="info"
          isInline
          isPlain
          className="pf-v6-u-mt-md"
          title={
            preview.count === 0
              ? 'No refundable hour among the rows shown'
              : `Refunds ${preview.count} hour(s), ${preview.total !== null ? formatPrice(preview.total) : 'an unknown amount'} incl. VAT`
          }
        >
          {truncated && `The preview only sees the newest ${usage.length} of ${server.usage.hours} hour rows.`}
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => currentRequest !== null && void send(currentRequest)}
          isLoading={busy}
          isDisabled={busy || currentRequest === null}
        >
          Refund
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerdaRefundServerModal;
